import { useMemo, useEffect, useState } from 'react';
import { zSceneManifest } from './manifest';
import { STYLE_PACKS, getGoogleFontsUrl, getStylePack } from './style-packs';
import { SceneProvider } from './scene-context';
import { SceneSectionShell } from './SceneSectionShell';
import { HeroSection } from './sections/HeroSection';
import { OpeningSequence } from './OpeningSequence';
import { useLenisScroll } from './useLenisScroll';
import { captureFrontendException } from '../../../infrastructure/lib/sentry';

const reportedErrors = new WeakSet<object>();

function reportInvalidManifest(error: object): void {
  // Deduplicate per-manifest to avoid spam on re-renders
  if (reportedErrors.has(error)) return;
  reportedErrors.add(error);
  console.warn('[SceneExperienceRenderer] invalid manifest', error);
  captureFrontendException(error).catch(() => {});
}

type Props = {
  manifest: unknown;
  /**
   * Set when this renderer is one of several simultaneous instances inside a
   * non-scrolling container — e.g. a template-picker grid card — rather than
   * the single full-page instance a real invitation view always is.
   *
   * `SceneExperienceRenderer` was built assuming exactly one instance ever
   * renders on a page: `useLenisScroll()` instantiates Lenis with no `wrapper`,
   * so it hijacks the *page's* native scroll globally, and `OpeningSequence`
   * sets `document.body.style.overflow = 'hidden'` on mount. Mounting several
   * instances at once (one per template card) meant several competing global
   * Lenis instances fighting over the same scroll target, plus a body-scroll
   * lock whose cleanup order is undefined across instances — the page's
   * scroll broke entirely and stayed broken after leaving the picker. Preview
   * mode skips both: no Lenis, no OpeningSequence gate (starts pre-opened).
   */
  preview?: boolean;
};

export function SceneExperienceRenderer({ manifest, preview = false }: Props) {
  const parsed = useMemo(() => zSceneManifest.safeParse(manifest), [manifest]);
  const [opened, setOpened] = useState(preview);
  useLenisScroll(preview);

  useEffect(() => {
    if (!parsed.success) return;
    const pack = parsed.data.template;
    const rawUrl = getGoogleFontsUrl(pack);
    // Validate the URL is a safe https:// Google Fonts URL before injecting into the DOM
    let url: string;
    try {
      const fontUrl = new URL(rawUrl);
      if (fontUrl.protocol !== 'https:' || !fontUrl.hostname.endsWith('fonts.googleapis.com')) return;
      url = fontUrl.toString();
    } catch {
      return;
    }
    if (!document.querySelector(`link[href="${url}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      document.head.appendChild(link);
    }
  }, [parsed]);

  useEffect(() => {
    if (preview || !parsed.success) return;

    const openingEnabled = parsed.data.opening?.enabled ?? true;
    const storageKey = parsed.data.opening?.skipStorageKey
      ?? `invitecloud:opened:${parsed.data.invitationId}`;

    if (!openingEnabled) {
      setOpened(true);
      return;
    }

    setOpened(sessionStorage.getItem(storageKey) === '1');
  }, [parsed, preview]);

  if (!parsed.success) {
    reportInvalidManifest(parsed.error);
    return null;
  }

  const data = parsed.data;
  const pack = getStylePack(data.template);
  const sorted = [...data.sections].sort((a, b) => a.order - b.order);
  const openingEnabled = data.opening?.enabled ?? true;

  return (
    <SceneProvider value={{ manifest: data, pack }}>
      <div
        className="relative font-sans"
        style={{ backgroundColor: pack.colors.background, color: pack.colors.text }}
      >
        {!preview && openingEnabled && !opened && <OpeningSequence onComplete={() => setOpened(true)} />}

        {/* Hero section */}
        <HeroSection hero={data.hero} />

        {/* Ceremony sections */}
        {sorted.map(section => (
          <SceneSectionShell key={section.sectionId} section={section} />
        ))}

        {/* Footer placeholder — replaced by T07 */}
        <footer
          className="py-16 text-center opacity-60 text-sm"
          style={{ color: pack.colors.text, fontFamily: pack.fonts.body }}
        >
          {data.footer.venue ? (
            <>
              <p>{data.footer.venue.name}</p>
              <p>{data.footer.venue.address}</p>
            </>
          ) : (
            <p>{data.footer.calendar.title}</p>
          )}
        </footer>
      </div>
    </SceneProvider>
  );
}
